import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Radio, AttentionBoxWithIcon } from '@p81-common/ui';
import { WithTooltip } from 'components/common/Elements/Elements';
import { links } from 'data/constants';
import { hasAccess, resources, access } from 'utils/permissions';
import createClasses from './styles';

const SIZE_LABEL_ID = 'gw-resize-size-label';

export const PLAN_TYPES = {
  REGULAR: 'regular',
  IMPROVED: 'improved',
  SUPREME: 'supreme'
} as const;

export type PlanType = typeof PLAN_TYPES[keyof typeof PLAN_TYPES];

// Sizes shown are typical - the real CPU/RAM comes from the region's provider,
// so the modal qualifies them rather than presenting them as guaranteed.
const TIERS: { value: PlanType; cores: number; memory: number }[] = [
  { value: PLAN_TYPES.IMPROVED, cores: 2, memory: 4 },
  { value: PLAN_TYPES.SUPREME, cores: 4, memory: 8 }
];

interface GwResizeModalProps {
  open: boolean;
  gatewayName: string;
  region?: string;
  currentPlanType?: PlanType | null;
  availablePlanTypes?: PlanType[];
  onClose: () => void;
  onProceed: (instancePlanType: PlanType) => void;
}

const GwResizeModal = ({
  open,
  gatewayName,
  region,
  currentPlanType,
  availablePlanTypes = [],
  onClose,
  onProceed
}: GwResizeModalProps) => {
  const { t } = useTranslation(['activity']);
  const [selected, setSelected] = useState<PlanType | null>(null);
  const classes = createClasses();

  useEffect(() => {
    setSelected(null);
  }, [open, currentPlanType]);

  const canSelectSupreme = hasAccess([
    resources.CLOUD_MANAGEMENT_RESIZE_SUPREME,
    access.WRITE
  ]);

  // Null when the tier can be picked. Otherwise the copy explaining why not,
  // so a gated row reads as gated instead of broken.
  const disabledReason = (value: PlanType) => {
    if (!availablePlanTypes.includes(value))
      return t('activity:gwResize.DISABLED_NOT_UPGRADE');

    if (value === PLAN_TYPES.SUPREME && !canSelectSupreme)
      return t('activity:gwResize.DISABLED_NO_PERMISSION');

    return null;
  };

  const isSelectable = (value: PlanType) => !disabledReason(value);

  const hasAnySelectable = TIERS.some(tier => isSelectable(tier.value));

  // Only offered on gateways below improved, where firewall granularity needs
  // a separate policy migration.
  const showGranularityNote =
    !currentPlanType || currentPlanType === PLAN_TYPES.REGULAR;

  const tierLabel = (value: PlanType, cores: number, memory: number) =>
    `${t(`activity:gwResize.TIER_${value.toUpperCase()}`)} - ${t(
      'activity:gwResize.TIER_SIZE',
      { cores, memory }
    )}`;

  // The radio's own label fills the row, so the whole card is one focusable
  // control rather than a click target only a pointer can reach.
  const renderTier = ({ value, cores, memory }: typeof TIERS[number]) => {
    const reason = disabledReason(value);

    const row = (
      <div
        className={cn(classes.option, {
          [classes.optionSelected]: selected === value,
          [classes.optionDisabled]: Boolean(reason)
        })}
      >
        <Radio
          checked={selected === value}
          onChange={() => setSelected(value)}
          value={value}
          name="gw-resize-plan-type"
          disabled={Boolean(reason)}
          formControlLabelProps={{
            label: tierLabel(value, cores, memory),
            classes: { root: classes.radio }
          }}
        />
      </div>
    );

    return reason ? (
      <WithTooltip key={value} text={reason}>
        {row as any}
      </WithTooltip>
    ) : (
      <React.Fragment key={value}>{row}</React.Fragment>
    );
  };

  return (
    <Modal
      id="gwResizeModal"
      open={open}
      dialogTitleProps={{ children: t('activity:gwResize.TITLE') }}
      dialogContentProps={{
        children: (
          <div className={classes.content}>
            <div className={classes.details}>
              <span className={classes.detailLabel}>
                {t('activity:gwResize.DETAIL_GATEWAY')}
              </span>
              <span className={classes.detailValue}>{gatewayName}</span>

              <span className={classes.detailLabel}>
                {t('activity:gwResize.DETAIL_CURRENT_SIZE')}
              </span>
              <span className={classes.detailValue}>
                {currentPlanType
                  ? t(`activity:gwResize.TIER_${currentPlanType.toUpperCase()}`)
                  : t('activity:gwResize.TIER_UNKNOWN')}
              </span>

              {region && (
                <>
                  <span className={classes.detailLabel}>
                    {t('activity:gwResize.DETAIL_REGION')}
                  </span>
                  <span className={classes.detailValue}>{region}</span>
                </>
              )}
            </div>

            {hasAnySelectable ? (
              <div className={classes.panel}>
                <div className={classes.label} id={SIZE_LABEL_ID}>
                  {t('activity:gwResize.SELECT_LABEL')}
                </div>
                <div role="radiogroup" aria-labelledby={SIZE_LABEL_ID}>
                  {TIERS.map(renderTier)}
                </div>
                <div className={classes.note}>
                  {t('activity:gwResize.SIZE_DISCLAIMER')}
                </div>
              </div>
            ) : (
              <div className={classes.emptyState}>
                {t('activity:gwResize.NO_OPTIONS')}
              </div>
            )}

            {/* Warnings describe what confirming would do, so they are noise
                when nothing can be selected. */}
            {hasAnySelectable && (
              <div className={classes.warning}>
                <AttentionBoxWithIcon
                  content={t('activity:gwResize.DISRUPTION_WARNING')}
                />
              </div>
            )}

            {hasAnySelectable && showGranularityNote && (
              <div className={classes.warning}>
                <AttentionBoxWithIcon
                  content={
                    <span>
                      {t('activity:gwResize.GRANULARITY_NOTE')}{' '}
                      <a
                        href={links.firewallGranularityPlaybook}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('activity:gwResize.GRANULARITY_LINK')}
                      </a>
                    </span>
                  }
                />
              </div>
            )}
          </div>
        )
      }}
      dialogActionsProps={{
        children: hasAnySelectable ? (
          <>
            <Button variant="text" color="third" onClick={onClose}>
              {t('activity:buttons.CANCEL')}
            </Button>
            <Button
              color="primary"
              onClick={() => selected && onProceed(selected)}
              disabled={!selected}
              data-testid="gw-resize-proceed"
            >
              {t('activity:gwResize.PROCEED')}
            </Button>
          </>
        ) : (
          <Button color="primary" onClick={onClose}>
            {t('activity:common.CLOSE')}
          </Button>
        )
      }}
      onClose={onClose}
    />
  );
};

export default GwResizeModal;
